import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle2, AlertTriangle, FileText, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Generic 3-step CSV import dialog with column mapping.
 *
 * Props:
 *   open                 - bool, controls visibility
 *   onClose              - callback to close the dialog
 *   onComplete           - callback after successful commit (receives result payload)
 *   title                - dialog title (e.g. "Dolmetscher importieren")
 *   previewEndpoint      - API path for preview step (e.g. "/interpreters/import-csv/preview")
 *   commitEndpoint       - API path for commit step (e.g. "/interpreters/import-csv/commit")
 *   templateFields       - optional string shown as format hint
 *   testidPrefix         - prefix for data-testids
 */
const CsvImportDialog = ({
  open,
  onClose,
  onComplete,
  title = 'CSV importieren',
  previewEndpoint,
  commitEndpoint,
  testidPrefix = 'csv-import',
}) => {
  const [step, setStep] = useState(1); // 1=upload, 2=mapping, 3=result
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null); // { import_id, headers, preview, target_fields, suggested_mapping, delimiter }
  const [mapping, setMapping] = useState({}); // { csv_header: target_field_key }
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(1);
    setPreview(null);
    setMapping({});
    setResult(null);
    setSkipDuplicates(true);
    setUploading(false);
    setCommitting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(previewEndpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      setMapping({ ...(res.data.suggested_mapping || {}) });
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'CSV konnte nicht gelesen werden');
    } finally {
      setUploading(false);
    }
  };

  const setMap = (header, target) => {
    setMapping(m => ({ ...m, [header]: target }));
  };

  // Target fields that must be mapped (required)
  const requiredMissing = () => {
    if (!preview) return [];
    const mappedTargets = new Set(Object.values(mapping).filter(Boolean));
    return preview.target_fields.filter(f => f.required && !mappedTargets.has(f.key));
  };

  // Duplicate target warning
  const duplicateTargets = () => {
    const counts = {};
    Object.values(mapping).forEach(t => { if (t) counts[t] = (counts[t] || 0) + 1; });
    return Object.entries(counts).filter(([, n]) => n > 1).map(([t]) => t);
  };

  const commit = async () => {
    const missing = requiredMissing();
    if (missing.length > 0) {
      toast.error(`Pflichtfelder fehlen: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    const dupes = duplicateTargets();
    if (dupes.length > 0) {
      toast.error(`Mehrfach zugeordnete Felder: ${dupes.join(', ')}`);
      return;
    }
    setCommitting(true);
    try {
      const res = await api.post(commitEndpoint, {
        import_id: preview.import_id,
        mapping,
        skip_duplicates: skipDuplicates,
      });
      setResult(res.data);
      setStep(3);
      onComplete?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setCommitting(false);
    }
  };

  if (!open) return null;

  const usedTargets = new Set(Object.values(mapping).filter(Boolean));

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={handleClose}
      data-testid={`${testidPrefix}-dialog`}
    >
      <div
        className="bg-white rounded-md max-w-5xl w-full my-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
              <span className={step >= 1 ? 'text-[#0F4C81] font-medium' : ''}>1. Datei hochladen</span>
              <ArrowRight className="w-3 h-3" />
              <span className={step >= 2 ? 'text-[#0F4C81] font-medium' : ''}>2. Spalten zuordnen</span>
              <ArrowRight className="w-3 h-3" />
              <span className={step >= 3 ? 'text-[#0F4C81] font-medium' : ''}>3. Ergebnis</span>
            </div>
          </div>
          <button
            onClick={handleClose}
            data-testid={`${testidPrefix}-close-btn`}
            className="text-slate-500 hover:text-slate-900 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {step === 1 && (
            <div className="text-center py-10">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#0F4C81]/10 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-[#0F4C81]" />
              </div>
              <p className="text-sm text-slate-600 mb-6 max-w-md mx-auto">
                Wählen Sie eine CSV-Datei aus. Im nächsten Schritt ordnen Sie die Spalten den Datenbankfeldern zu.
                Trennzeichen werden automatisch erkannt (<code className="font-mono text-xs bg-slate-100 px-1">,</code> oder <code className="font-mono text-xs bg-slate-100 px-1">;</code>).
              </p>
              <label
                className="inline-flex items-center gap-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-6 py-3 text-sm cursor-pointer disabled:opacity-50"
                data-testid={`${testidPrefix}-upload-label`}
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Wird geladen...' : 'CSV-Datei wählen'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  disabled={uploading}
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid={`${testidPrefix}-file-input`}
                />
              </label>
            </div>
          )}

          {step === 2 && preview && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm text-slate-700">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <strong>{preview.headers.length}</strong> Spalten erkannt, Vorschau der ersten <strong>{preview.preview.length}</strong> Zeile(n).
                    Trennzeichen: <code className="font-mono text-xs bg-white px-1 border">{preview.delimiter === ';' ? 'Semikolon' : 'Komma'}</code>
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={skipDuplicates}
                      onChange={(e) => setSkipDuplicates(e.target.checked)}
                      data-testid={`${testidPrefix}-skip-duplicates`}
                      className="w-4 h-4"
                    />
                    Duplikate (gleiche E-Mail) überspringen
                  </label>
                </div>
              </div>

              {/* Mapping table */}
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm" data-testid={`${testidPrefix}-mapping-table`}>
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4 w-1/3">
                        CSV-Spalte
                      </th>
                      <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4 w-1/3">
                        Zuordnung (DB-Feld)
                      </th>
                      <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4 w-1/3">
                        Beispielwerte
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.headers.map((h) => {
                      const currentTarget = mapping[h] || '';
                      const samples = preview.preview.slice(0, 3).map(r => r[h] || '').filter(Boolean);
                      return (
                        <tr key={h} className="border-b border-slate-100">
                          <td className="py-2 px-4 font-mono text-xs text-slate-800">{h}</td>
                          <td className="py-2 px-4">
                            <select
                              value={currentTarget}
                              onChange={(e) => setMap(h, e.target.value)}
                              data-testid={`${testidPrefix}-map-${h}`}
                              className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
                            >
                              <option value="">— Spalte ignorieren —</option>
                              {preview.target_fields.map(f => {
                                const takenByOther = usedTargets.has(f.key) && currentTarget !== f.key;
                                return (
                                  <option key={f.key} value={f.key} disabled={takenByOther}>
                                    {f.label}{f.required ? ' *' : ''}{takenByOther ? ' (bereits zugeordnet)' : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td className="py-2 px-4 text-xs text-slate-600">
                            {samples.length > 0 ? samples.join(' · ') : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Required status */}
              <div className="text-xs text-slate-600">
                {(() => {
                  const missing = requiredMissing();
                  if (missing.length === 0) {
                    return (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <CheckCircle2 className="w-4 h-4" /> Alle Pflichtfelder zugeordnet
                      </span>
                    );
                  }
                  return (
                    <span className="inline-flex items-center gap-1 text-amber-700">
                      <AlertTriangle className="w-4 h-4" />
                      Fehlend: {missing.map(f => f.label).join(', ')}
                    </span>
                  );
                })()}
              </div>
            </div>
          )}

          {step === 3 && result && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-700 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-green-900">Import abgeschlossen</div>
                  <div className="text-sm text-green-800 mt-1">
                    <strong data-testid={`${testidPrefix}-imported-count`}>{result.imported}</strong> Datensätze importiert,{' '}
                    <strong data-testid={`${testidPrefix}-skipped-count`}>{result.skipped}</strong> übersprungen.
                  </div>
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="border border-amber-200 rounded-md">
                  <div className="bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 border-b border-amber-200">
                    Hinweise / Fehler ({result.errors.length})
                  </div>
                  <ul className="divide-y divide-amber-100 max-h-48 overflow-y-auto" data-testid={`${testidPrefix}-errors-list`}>
                    {result.errors.map((e, i) => (
                      <li key={i} className="px-4 py-1.5 text-xs text-slate-700 font-mono">{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-md">
          <div className="text-xs text-slate-500">
            {step === 2 && preview && `${preview.headers.length} Spalten · ${Object.values(mapping).filter(Boolean).length} zugeordnet`}
          </div>
          <div className="flex gap-2">
            {step === 2 && (
              <button
                type="button"
                onClick={() => { reset(); }}
                data-testid={`${testidPrefix}-back-btn`}
                className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-200 rounded-md"
              >
                Andere Datei wählen
              </button>
            )}
            {step === 3 ? (
              <button
                type="button"
                onClick={handleClose}
                data-testid={`${testidPrefix}-done-btn`}
                className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-5 py-2 text-sm"
              >
                Schließen
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-200 rounded-md"
                >
                  Abbrechen
                </button>
                {step === 2 && (
                  <button
                    type="button"
                    onClick={commit}
                    disabled={committing}
                    data-testid={`${testidPrefix}-commit-btn`}
                    className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-5 py-2 text-sm disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    {committing ? 'Importiere...' : 'Import starten'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvImportDialog;
